import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import PhoneInput from '@/components/PhoneInput'
import {ArrowRight, ArrowLeft, Loader2, AlertCircle, } from 'lucide-react'

const EMAIL_FN_URL =
  'https://api.lumi.new/v1/functions/p427216140118241280/send-welcome-email'

const MOTIVOS = [
  'Quero usar para cuidar da minha saúde e bem-estar',
  'Quero aprender para ajudar pessoas próximas',
  'Tenho curiosidade sobre aromaterapia',
  'Quero entender se posso trabalhar com isso',
]

const INTERESSE_RENDA = [
  'Não, quero só aprender por agora',
  'Talvez, mas ainda não pensei nisso',
  'Sim, tenho interesse em entender como funciona',
]

function CursoInscricaoForm() {
  const navigate = useNavigate()

  // Parâmetro indicado_por da URL (fallback "monica")
  const [indicadoPor, setIndicadoPor] = useState('monica')

  // Formulário
  const [nome, setNome] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [email, setEmail] = useState('')
  const [motivo, setMotivo] = useState('')
  const [interesseRenda, setInteresseRenda] = useState('')

  // Estado
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState('')

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const indicado = params.get('indicado_por')?.trim()
    if (indicado) {
      setIndicadoPor(indicado)
    }
    window.scrollTo(0, 0)
  }, [])

  function voltarParaCurso() {
    const qs = indicadoPor && indicadoPor !== 'monica'
      ? `?indicado_por=${encodeURIComponent(indicadoPor)}`
      : ''
    navigate(`/curso${qs}`)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErro('')

    if (!nome.trim() || !whatsapp.trim() || !email.trim()) {
      setErro('Preencha todos os campos obrigatórios.')
      return
    }
    if (!motivo) {
      setErro('Selecione o motivo pelo qual quer aprender.')
      return
    }
    if (!interesseRenda) {
      setErro('Responda a pergunta sobre interesse futuro.')
      return
    }

    setEnviando(true)
    try {
      const agora = new Date().toISOString()
      const registro = {
        nome: nome.trim(),
        whatsapp: whatsapp.trim(),
        email: email.trim(),
        motivoInscricao: motivo,
        interesseRenda,
        origem: 'Curso',
        cursoId: 'uma-gota',
        indicadoPor,
        statusInscricao: 'Inscrita',
        etapaAtual: 'inscricao',
        dataInscricao: agora,
        createdAt: agora,
        updatedAt: agora,
      }

      const novoRegistro = await lumi.entities.capacitacao.create(registro)

      // Salvar identificação na sessão para que /curso-aulas vincule
      // corretamente a conclusão do curso a este registro em `capacitacao`.
      try {
        const id = (novoRegistro as any)?._id || (novoRegistro as any)?.id || ''
        if (id) sessionStorage.setItem('capacitacao_id', id)
        sessionStorage.setItem('capacitacao_whatsapp', whatsapp.trim())
        sessionStorage.setItem('capacitacao_nome', nome.trim())
        sessionStorage.setItem('capacitacao_email', email.trim())
      } catch {
        // ignore
      }

      // Disparar e-mail de boas-vindas (não bloqueia o redirecionamento)
      void fetch(EMAIL_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'curso_inscricao_publica',
          nome: nome.trim(),
          email: email.trim(),
        }),
      }).catch(err => console.error('[curso-inscricao] erro ao enviar e-mail', err))

      // Redireciona direto para o player do curso, sem mensagem intermediária
      navigate('/curso-aulas')
    } catch (err) {
      console.error('[curso-inscricao] erro ao salvar', err)
      setErro('Não foi possível concluir sua inscrição. Tente novamente.')
      setEnviando(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#F8F4EF]">
      {/* Header */}
      <header className="bg-white border-b border-[#EDE0D4] sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <p className="font-cormorant text-lg sm:text-xl font-semibold text-[#2D6A4F] truncate">
            Diagnóstico Saúde e Emoções
          </p>
          <button
            type="button"
            onClick={voltarParaCurso}
            className="inline-flex items-center gap-1.5 text-sm text-[#5F5E5A] hover:text-[#2D6A4F] transition-colors"
          >
            <ArrowLeft size={16} />
            Voltar
          </button>
        </div>
      </header>

      <main className="px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-2xl mx-auto">
          {/* Título + subtítulo */}
          <div className="text-center mb-10">
            <h1 className="font-cormorant text-3xl sm:text-5xl font-semibold text-[#2C2C2A] mb-4 leading-tight">
              Garanta seu acesso gratuito
            </h1>
            <p className="text-[#5F5E5A] text-base sm:text-lg leading-relaxed">
              Preencha os dados abaixo para acessar o curso Uma Gota de Equilíbrio.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl p-6 sm:p-8 border border-[#EDE0D4] shadow-sm space-y-6"
          >
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-[#2C2C2A] mb-1.5">
                Nome completo <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={nome}
                onChange={e => setNome(e.target.value)}
                placeholder="Seu nome completo"
                required
                className="w-full px-4 py-3 rounded-xl border border-[#EDE0D4] bg-[#F8F4EF] text-[#2C2C2A] placeholder-[#B08968]/60 focus:outline-none focus:border-[#52B788] focus:ring-1 focus:ring-[#52B788] transition-colors"
              />
            </div>

            {/* WhatsApp */}
            <div>
              <label className="block text-sm font-medium text-[#2C2C2A] mb-1.5">
                WhatsApp <span className="text-red-500">*</span>
              </label>
              <PhoneInput
                value={whatsapp}
                onChange={setWhatsapp}
                required
              />
            </div>

            {/* E-mail */}
            <div>
              <label className="block text-sm font-medium text-[#2C2C2A] mb-1.5">
                E-mail <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                className="w-full px-4 py-3 rounded-xl border border-[#EDE0D4] bg-[#F8F4EF] text-[#2C2C2A] placeholder-[#B08968]/60 focus:outline-none focus:border-[#52B788] focus:ring-1 focus:ring-[#52B788] transition-colors"
              />
            </div>

            {/* Pergunta 1 */}
            <div>
              <label className="block text-sm font-medium text-[#2C2C2A] mb-3">
                Por que você quer aprender mais sobre óleos essenciais?{' '}
                <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {MOTIVOS.map(op => (
                  <label
                    key={op}
                    className={`flex items-start gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all text-sm ${
                      motivo === op
                        ? 'border-[#52B788] bg-[#D8F3DC]'
                        : 'border-[#EDE0D4] bg-[#F8F4EF] hover:border-[#52B788]/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="motivo"
                      value={op}
                      checked={motivo === op}
                      onChange={() => setMotivo(op)}
                      className="accent-[#2D6A4F] mt-0.5 flex-shrink-0"
                    />
                    <span className="text-[#2C2C2A]">{op}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Pergunta 2 */}
            <div>
              <label className="block text-sm font-medium text-[#2C2C2A] mb-3">
                No futuro, você teria interesse em compartilhar os óleos essenciais e gerar uma renda extra com isso?{' '}
                <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {INTERESSE_RENDA.map(op => (
                  <label
                    key={op}
                    className={`flex items-start gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all text-sm ${
                      interesseRenda === op
                        ? 'border-[#52B788] bg-[#D8F3DC]'
                        : 'border-[#EDE0D4] bg-[#F8F4EF] hover:border-[#52B788]/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="interesseRenda"
                      value={op}
                      checked={interesseRenda === op}
                      onChange={() => setInteresseRenda(op)}
                      className="accent-[#2D6A4F] mt-0.5 flex-shrink-0"
                    />
                    <span className="text-[#2C2C2A]">{op}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Erro */}
            {erro && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{erro}</span>
              </div>
            )}

            {/* Botão */}
            <button
              type="submit"
              disabled={enviando}
              className="w-full flex items-center justify-center gap-2 bg-[#2D6A4F] text-white font-medium px-6 py-4 rounded-full hover:bg-[#1f4f3a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-base"
            >
              {enviando ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Confirmando inscrição…
                </>
              ) : (
                <>
                  Confirmar minha inscrição
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        </div>
      </main>
    </div>
  )
}

export default CursoInscricaoForm
